/**
 * Phone number utilities for Finnish phone numbers
 * Handles normalization to E.164 format and display formatting
 */

/**
 * Normalizes phone number to E.164 international format
 * - Strips whitespace and common separators (dashes, dots, parentheses)
 * - If starts with +: returns as-is
 * - If starts with 0: removes leading zero and prepends +358
 * - Otherwise: prepends +358
 *
 * @example
 * normalizePhoneNumber('0405826303') // '+358405826303'
 * normalizePhoneNumber('040 582 6303') // '+358405826303'
 * normalizePhoneNumber('040-582-6303') // '+358405826303'
 * normalizePhoneNumber('(040) 582 6303') // '+358405826303'
 * normalizePhoneNumber('+358405826303') // '+358405826303'
 * normalizePhoneNumber(null) // null
 */
export function normalizePhoneNumber(
  input: string | null | undefined,
): string | null {
  if (!input) return null

  // Remove whitespace and common separators that users type for readability
  const cleaned = input.trim().replace(/[\s\-().]/g, '')
  if (!cleaned) return null

  // Already has international prefix
  if (cleaned.startsWith('+')) {
    return cleaned
  }

  // Remove leading zero if present (Finnish local format)
  const withoutLeadingZero = cleaned.startsWith('0')
    ? cleaned.substring(1)
    : cleaned

  // Add Finnish country code
  return `+358${withoutLeadingZero}`
}

/**
 * Formats phone number for display
 * Converts +358405826303 to 040 582 6303 (Finnish local format with spaces)
 *
 * @example
 * formatPhoneNumber('+358405826303') // '040 582 6303'
 * formatPhoneNumber('+358201234567') // '020 123 4567'
 * formatPhoneNumber('+1234567890') // '+1 234 567 890' (non-Finnish, generic format)
 */
export function formatPhoneNumber(input: string | null | undefined): string {
  if (!input) return ''

  const cleaned = input.trim()

  // Handle Finnish numbers (+358)
  if (cleaned.startsWith('+358')) {
    const localNumber = cleaned.substring(4) // Remove +358
    const withLeadingZero = `0${localNumber}` // Add leading zero back

    // Format as: 0XX XXX XXXX or 0XX XXX XXX (depending on length)
    if (withLeadingZero.length === 10) {
      // Mobile: 040 582 6303
      return `${withLeadingZero.substring(0, 3)} ${withLeadingZero.substring(3, 6)} ${withLeadingZero.substring(6)}`
    } else if (withLeadingZero.length === 11) {
      // Some formats: 020 123 4567
      return `${withLeadingZero.substring(0, 3)} ${withLeadingZero.substring(3, 6)} ${withLeadingZero.substring(6)}`
    }

    // Fallback for other lengths
    return withLeadingZero
  }

  // For non-Finnish numbers, add spaces every 3 digits after country code
  const match = cleaned.match(/^(\+\d{1,3})(\d+)$/)
  if (match) {
    const countryCode = match[1]
    const number = match[2]
    const formattedNumber = number.match(/.{1,3}/g)?.join(' ') || number
    return `${countryCode} ${formattedNumber}`
  }

  return cleaned
}

/**
 * Validates phone number format
 * Checks if phone number is in valid E.164 format
 *
 * @example
 * isValidPhoneFormat('+358405826303') // true
 * isValidPhoneFormat('0405826303') // false (not international format)
 * isValidPhoneFormat('+358') // false (too short)
 */
export function isValidPhoneFormat(phone: string): boolean {
  // E.164 format: + followed by 1-3 digit country code and 4-14 digit number
  // Total length: 7-17 characters (including +)
  return /^\+[1-9]\d{6,14}$/.test(phone)
}
